import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "../../../components/AppToast";
import {
  CUSTOMER_TYPES,
  CUSTOMER_STATUS,
  CUSTOMER_SEGMENTS,
  SALES_CHANNELS,
  PORTAL_USER_ROLES,
  PRICING_BASIS,
  CURRENCIES,
  MOCK_USERS,
  CONTACT_ROLES,
} from "./orgConstants";
import { getGlobalOptions } from "../../../services/auth/selfSignUp";
import { enumGlobalOptionData } from "../../../utils/enumGlobalOptionData";
import { convertForSelect } from "../../../utils/convertApiData";
import {
  customerInitial,
  emptyContact,
  emptyPortalAdmin,
  normalizeCustomerRecord,
  syncStructuredAddressesFromText,
  normalizeCustomerContacts,
} from "./customerModel";
import { PHONE_COUNTRY_CODES_LIST } from "../../../data/static";

function FieldLabel({ htmlFor, label, required, tooltip }) {
  return (
    <label htmlFor={htmlFor} className="form-label fw-semibold small text-body-secondary mb-1">
      {label}
      {required ? <span className="text-danger ms-1">*</span> : null}
      {tooltip ? (
        <span className="ms-1 text-muted" title={tooltip}>
          <i className="bi bi-info-circle" aria-hidden />
        </span>
      ) : null}
    </label>
  );
}

/** Merged section subtitle inside a tab */
function FormBlock({ title, subtitle, iconClass, children }) {
  return (
    <section className="cust-form-block mb-4 pb-2">
      <div className="d-flex align-items-start gap-2 mb-3">
        {iconClass ? <i className={`${iconClass} text-primary mt-1`} aria-hidden /> : null}
        <div>
          <div className="h6 fw-semibold mb-0 text-body">{title}</div>
          {subtitle ? <p className="text-muted small mb-0 mt-1">{subtitle}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function normalizeTenantIdString(tenantId) {
  if (tenantId == null || tenantId === "") return "";
  if (typeof tenantId === "object" && tenantId._id != null) return String(tenantId._id);
  return String(tenantId || "");
}

const CUST_TABS = [
  { id: "profile", label: "Profile & mapping", icon: "bi-person-vcard" },
  { id: "location", label: "Addresses & finance", icon: "bi-geo-alt" },
  { id: "operations", label: "Access & logistics", icon: "bi-truck" },
  { id: "people", label: "Contacts & notes", icon: "bi-people" },
];

/** Same dial codes as typical partner / auth flows. */
const PHONE_COUNTRY_CODES = PHONE_COUNTRY_CODES_LIST

export default function CustomerOnboardingForm({ org, tenantId, customer, formKey, onSave, onCancel }) {
  const auditUser = useSelector((s) => {
    const u = s?.login?.userDetail;
    if (!u) return "—";
    return String(u.email ?? u.userName ?? u.name ?? u._id ?? "—");
  });

  const tenantIdStr = useMemo(() => normalizeTenantIdString(tenantId), [tenantId]);

  const [form, setForm] = useState(() => normalizeCustomerRecord(customer || customerInitial(), org?.id));
  const [errors, setErrors] = useState({});
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [custTab, setCustTab] = useState("profile");
  const [moduleOptions, setModuleOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ModuleTypeData = await getGlobalOptions(enumGlobalOptionData.SYS_CAP);
        if (!cancelled) setModuleOptions(convertForSelect(ModuleTypeData) || []);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.message || "Could not load module access list.");
          setModuleOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setForm(normalizeCustomerRecord(customer || customerInitial(), org?.id));
    setErrors({});
    setSameAsBilling(false);
    setCustTab("profile");
  }, [formKey, org?.id, customer]);

  useEffect(() => {
    if (sameAsBilling) {
      setForm((f) => ({ ...f, shippingAddressText: f.billingAddressText }));
    }
  }, [sameAsBilling, form.billingAddressText]);

  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const setPortalAdminField = (k, v) => {
    setForm((p) => ({
      ...p,
      portalAdmin: { ...emptyPortalAdmin(), ...p.portalAdmin, [k]: v },
    }));
    setErrors((e) => ({ ...e, portalAdminPassword: undefined }));
  };

  const mappedOrgLabel = org ? `${org.code} — ${org.name}` : "—";

  const moduleSelectValue = useMemo(
    () => (moduleOptions || []).filter((o) => (form.moduleAccess || []).includes(o.value)),
    [moduleOptions, form.moduleAccess]
  );

  const save = async () => {
    const e = {};
    if (!form.customerCode?.trim()) e.customerCode = "Supplier code is required.";
    if (!form.customerName?.trim()) e.customerName = "Supplier name is required.";
    if (!String(form.creditLimit ?? "").trim()) e.creditLimit = "Credit limit is required.";
    const pa = form.portalAdmin || emptyPortalAdmin();
    if (form.portalLogin && pa.password?.trim() && pa.password !== pa.confirmPassword) {
      e.portalAdminPassword = "Portal admin password and confirmation must match.";
    }
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Fix validation errors.");
      if (e.portalAdminPassword) setCustTab("operations");
      else if (e.creditLimit) setCustTab("location");
      else setCustTab("profile");
      return;
    }
    const nowIso = new Date().toISOString();
    const synced = syncStructuredAddressesFromText(form);
    const customerId = synced.customerId?.trim() || `CUST-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const portalAdminOut = { ...emptyPortalAdmin(), ...(synced.portalAdmin || {}) };
    if (!portalAdminOut.password?.trim()) {
      delete portalAdminOut.password;
      delete portalAdminOut.confirmPassword;
    }

    const payload = {
      ...synced,
      customerId,
      orgMappingId: org?.id || "",
      moduleAccess: Array.isArray(synced.moduleAccess) ? synced.moduleAccess : [],
      portalAdmin: portalAdminOut,
      customerContacts: normalizeCustomerContacts(synced.customerContacts).filter((c) => c.name || c.email || c.mobile || c.role),
      createdBy: synced.createdOn ? synced.createdBy : auditUser,
      createdOn: synced.createdOn || nowIso,
      updatedBy: auditUser,
      updatedOn: nowIso,
    };
    try {
      const ret = onSave(payload);
      if (ret && typeof ret.then === "function") await ret;
      toast.success("Supplier saved.");
    } catch {
      /* Parent / API layer shows error toast */
    }
  };

  const salesOwnerOptions = MOCK_USERS;

  return (
    <div className="customer-onboarding-form">
      <ul className="nav nav-tabs org-workspace-tabs customer-onboard-tabs border-0 gap-1 mb-0 flex-wrap" role="tablist">
        {CUST_TABS.map((t) => (
          <li className="nav-item" key={t.id} role="presentation">
            <button
              type="button"
              className={`nav-link rounded-top ${custTab === t.id ? "active" : ""}`}
              onClick={() => setCustTab(t.id)}
              role="tab"
              aria-selected={custTab === t.id}
            >
              <i className={`${t.icon} me-1`} aria-hidden />
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="customer-onboard-pan border border-top-0 rounded-bottom-3 bg-white px-3 px-md-4 py-4 shadow-sm">
        {custTab === "profile" && (
          <>
            <FormBlock title="Identity" subtitle="Codes, naming, classification, and hierarchy." iconClass="bi-person-badge">
              <div className="row g-3">
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-id" label="Supplier ID" tooltip="Auto-generated on save." />
                  <input id="c-id" className="form-control form-control-sm bg-body-secondary" readOnly value={form.customerId || "— (on save)"} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-code" label="Supplier code" required />
                  <input id="c-code" className={`form-control form-control-sm ${errors.customerCode ? "is-invalid" : ""}`} value={form.customerCode} onChange={(e) => setField("customerCode", e.target.value)} />
                  {errors.customerCode && <div className="invalid-feedback d-block">{errors.customerCode}</div>}
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-name" label="Supplier name" required />
                  <input id="c-name" className={`form-control form-control-sm ${errors.customerName ? "is-invalid" : ""}`} value={form.customerName} onChange={(e) => setField("customerName", e.target.value)} />
                  {errors.customerName && <div className="invalid-feedback d-block">{errors.customerName}</div>}
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-sn" label="Supplier short name" />
                  <input id="c-sn" className="form-control form-control-sm" value={form.customerShortName} onChange={(e) => setField("customerShortName", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-ty" label="Supplier type" />
                  <select id="c-ty" className="form-select form-select-sm" value={form.customerType} onChange={(e) => setField("customerType", e.target.value)}>
                    {CUSTOMER_TYPES.map((ty) => (
                      <option key={ty.value} value={ty.value}>
                        {ty.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-gr" label="Supplier group" />
                  <input id="c-gr" className="form-control form-control-sm" value={form.customerGroup} onChange={(e) => setField("customerGroup", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-seg" label="Supplier segment" />
                  <select id="c-seg" className="form-select form-select-sm" value={form.customerSegment} onChange={(e) => setField("customerSegment", e.target.value)}>
                    {CUSTOMER_SEGMENTS.map((s) => (
                      <option key={s.value || "x"} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-par" label="Parent supplier" tooltip="Optional." />
                  <input id="c-par" className="form-control form-control-sm" value={form.parentCustomerId} onChange={(e) => setField("parentCustomerId", e.target.value)} placeholder="Code or ID" />
                </div>
              </div>
            </FormBlock>

            <FormBlock title="Organization & market" subtitle="Tenant, linked org, channels, and region." iconClass="bi-diagram-3">
              <div className="row g-3">
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-tnt" label="Tenant ID" />
                  <input id="c-tnt" className="form-control form-control-sm bg-body-secondary" readOnly value={tenantIdStr || "—"} />
                </div>
                <div className="col-md-8">
                  <FieldLabel htmlFor="c-oid" label="Organization ID (linked)" />
                  <input id="c-oid" className="form-control form-control-sm bg-body-secondary" readOnly value={mappedOrgLabel} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-ch" label="Sales channel" />
                  <select id="c-ch" className="form-select form-select-sm" value={form.salesChannel} onChange={(e) => setField("salesChannel", e.target.value)}>
                    {SALES_CHANNELS.map((ch) => (
                      <option key={ch.value || "e"} value={ch.value}>
                        {ch.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-mkt" label="Market / region" />
                  <input id="c-mkt" className="form-control form-control-sm" value={form.marketRegion} onChange={(e) => setField("marketRegion", e.target.value)} />
                </div>
              </div>
            </FormBlock>

            <FormBlock title="Status & validity" subtitle="Lifecycle dates for this supplier record." iconClass="bi-calendar-check">
              <div className="row g-3">
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-st" label="Supplier status" />
                  <select id="c-st" className="form-select form-select-sm" value={form.customerStatus} onChange={(e) => setField("customerStatus", e.target.value)}>
                    {CUSTOMER_STATUS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-ef" label="Effective from" />
                  <input id="c-ef" type="date" className="form-control form-control-sm" value={form.customerEffectiveFrom} onChange={(e) => setField("customerEffectiveFrom", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-et" label="Effective to" />
                  <input id="c-et" type="date" className="form-control form-control-sm" value={form.customerEffectiveTo} onChange={(e) => setField("customerEffectiveTo", e.target.value)} />
                </div>
              </div>
            </FormBlock>
          </>
        )}

        {custTab === "location" && (
          <>
            <FormBlock title="Addresses" subtitle="Billing and shipping — use one flow for both locations." iconClass="bi-geo-alt">
              <div className="mb-3">
                <FieldLabel htmlFor="c-bn" label="Billing name" />
                <input id="c-bn" className="form-control form-control-sm" value={form.billingName} onChange={(e) => setField("billingName", e.target.value)} />
              </div>
              <div className="mb-2">
                <FieldLabel htmlFor="c-bill" label="Billing address" />
                <textarea id="c-bill" className="form-control form-control-sm font-monospace" rows={4} value={form.billingAddressText} onChange={(e) => setField("billingAddressText", e.target.value)} placeholder="Street, city, state, postal code…" />
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="sameBill" checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} />
                <label className="form-check-label small" htmlFor="sameBill">
                  Same as billing address
                </label>
              </div>
              <div>
                <FieldLabel htmlFor="c-ship" label="Shipping address" />
                <textarea id="c-ship" className="form-control form-control-sm font-monospace" rows={4} value={form.shippingAddressText} onChange={(e) => setField("shippingAddressText", e.target.value)} disabled={sameAsBilling} placeholder="Ship-to address…" />
              </div>
            </FormBlock>

            <FormBlock title="Commercial terms" subtitle="Currency, limits, and how pricing applies." iconClass="bi-currency-exchange">
              <div className="row g-3">
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-cur" label="Currency" />
                  <select id="c-cur" className="form-select form-select-sm" value={form.orderCurrency} onChange={(e) => setField("orderCurrency", e.target.value)}>
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-pt" label="Payment terms" />
                  <input id="c-pt" className="form-control form-control-sm" value={form.paymentTerms} onChange={(e) => setField("paymentTerms", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="creditLimit" label="Credit limit" required />
                  <input
                    id="creditLimit"
                    type="number"
                    min="0"
                    step="any"
                    className={`form-control form-control-sm ${errors.creditLimit ? "is-invalid" : ""}`}
                    value={form.creditLimit}
                    onChange={(e) => setField("creditLimit", e.target.value)}
                    placeholder="e.g. 5000000"
                  />
                  {errors.creditLimit && <div className="invalid-feedback d-block">{errors.creditLimit}</div>}
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-pl" label="Price list ID" tooltip="Optional." />
                  <input id="c-pl" className="form-control form-control-sm" value={form.priceListId} onChange={(e) => setField("priceListId", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-pb" label="Pricing basis" />
                  <select id="c-pb" className="form-select form-select-sm" value={form.pricingBasis} onChange={(e) => setField("pricingBasis", e.target.value)}>
                    {PRICING_BASIS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </FormBlock>

            <FormBlock title="Tax & registration" subtitle="GST / VAT and company references in one place." iconClass="bi-file-earmark-text">
              <div className="row g-3">
                <div className="col-md-6">
                  <FieldLabel htmlFor="c-tax" label="Tax registration number" tooltip="GST / VAT." />
                  <input id="c-tax" className="form-control form-control-sm" value={form.taxRegistrationNumber} onChange={(e) => setField("taxRegistrationNumber", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <FieldLabel htmlFor="c-pan" label="PAN / registration ref" />
                  <input id="c-pan" className="form-control form-control-sm" value={form.panRegistrationRef} onChange={(e) => setField("panRegistrationRef", e.target.value)} />
                </div>
              </div>
            </FormBlock>
          </>
        )}

        {custTab === "operations" && (
          <>
            <FormBlock
              title="Module access"
              subtitle="Capability modules (same list as partner create: SYS_CAP global options)."
              iconClass="bi-grid-3x3-gap"
            >
              <Select
                inputId="c-module-access"
                isMulti
                isClearable
                options={moduleOptions}
                value={moduleSelectValue}
                onChange={(opts) => setField("moduleAccess", opts?.map((o) => o.value) ?? [])}
                classNamePrefix="react-select"
                placeholder="Select modules…"
              />
              <div className="form-text small">Stored as an array of module ids on save.</div>
            </FormBlock>

            <FormBlock title="Portal access" subtitle="Who can sign in and what they can do." iconClass="bi-shield-lock">
              <div className="form-check form-switch mb-3">
                <input className="form-check-input" type="checkbox" id="c-portal" checked={form.portalLogin} onChange={(e) => setField("portalLogin", e.target.checked)} />
                <label className="form-check-label small" htmlFor="c-portal">
                  Portal login allowed
                </label>
              </div>
              {form.portalLogin && (
                <div className="row g-3">
                  <div className="col-md-4">
                    <FieldLabel htmlFor="c-pem" label="Portal username / email" />
                    <input id="c-pem" type="email" className="form-control form-control-sm" value={form.portalUsernameEmail} onChange={(e) => setField("portalUsernameEmail", e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel htmlFor="c-prole" label="Portal user role" />
                    <select id="c-prole" className="form-select form-select-sm" value={form.portalUserRole} onChange={(e) => setField("portalUserRole", e.target.value)}>
                      {PORTAL_USER_ROLES.map((r) => (
                        <option key={r.value || "r"} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="c-ord" checked={form.salesOrderEligible} onChange={(e) => setField("salesOrderEligible", e.target.checked)} />
                      <label className="form-check-label small" htmlFor="c-ord">
                        Order creation allowed
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </FormBlock>

            {form.portalLogin && (
              <FormBlock
                title="Portal administrator"
                subtitle="Initial portal admin user (sent as portalAdmin on create / update)."
                iconClass="bi-person-badge"
              >
                <div className="row g-3">
                  <div className="col-md-4">
                    <FieldLabel htmlFor="c-pa-fn" label="First name" />
                    <input
                      id="c-pa-fn"
                      className="form-control form-control-sm"
                      value={form.portalAdmin?.firstName ?? ""}
                      onChange={(e) => setPortalAdminField("firstName", e.target.value)}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel htmlFor="c-pa-ln" label="Last name" />
                    <input
                      id="c-pa-ln"
                      className="form-control form-control-sm"
                      value={form.portalAdmin?.lastName ?? ""}
                      onChange={(e) => setPortalAdminField("lastName", e.target.value)}
                      autoComplete="family-name"
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel htmlFor="c-pa-un" label="Username / email" />
                    <input
                      id="c-pa-un"
                      type="email"
                      className="form-control form-control-sm"
                      value={form.portalAdmin?.username ?? ""}
                      onChange={(e) => setPortalAdminField("username", e.target.value)}
                      autoComplete="username"
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldLabel htmlFor="c-pa-cc" label="Country code" />
                    <select
                      id="c-pa-cc"
                      className="form-select form-select-sm"
                      value={form.portalAdmin?.countryCode ?? "+91"}
                      onChange={(e) => setPortalAdminField("countryCode", e.target.value)}
                    >
                      {PHONE_COUNTRY_CODES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <FieldLabel htmlFor="c-pa-mob" label="Mobile" />
                    <input
                      id="c-pa-mob"
                      className="form-control form-control-sm"
                      value={form.portalAdmin?.mobile ?? ""}
                      onChange={(e) => setPortalAdminField("mobile", e.target.value)}
                      autoComplete="tel"
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldLabel htmlFor="c-pa-pw" label="Password" />
                    <input
                      id="c-pa-pw"
                      type="password"
                      className="form-control form-control-sm"
                      value={form.portalAdmin?.password ?? ""}
                      onChange={(e) => setPortalAdminField("password", e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldLabel htmlFor="c-pa-pw2" label="Confirm password" />
                    <input
                      id="c-pa-pw2"
                      type="password"
                      className={`form-control form-control-sm ${errors.portalAdminPassword ? "is-invalid" : ""}`}
                      value={form.portalAdmin?.confirmPassword ?? ""}
                      onChange={(e) => setPortalAdminField("confirmPassword", e.target.value)}
                      autoComplete="new-password"
                    />
                    {errors.portalAdminPassword && <div className="invalid-feedback d-block">{errors.portalAdminPassword}</div>}
                  </div>
                </div>
              </FormBlock>
            )}

            <FormBlock title="Fulfillment & sales owner" subtitle="Default ship point and account owner." iconClass="bi-truck">
              <div className="row g-3">
                <div className="col-md-6">
                  <FieldLabel htmlFor="c-wh" label="Default warehouse" />
                  <input id="c-wh" className="form-control form-control-sm" value={form.defaultWarehouse} onChange={(e) => setField("defaultWarehouse", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Sales owner" tooltip="Select user." />
                  <Select
                    inputId="c-so"
                    isClearable
                    options={salesOwnerOptions}
                    value={salesOwnerOptions.find((o) => o.value === form.salesOwner) ?? null}
                    onChange={(opt) => setField("salesOwner", opt?.value ?? "")}
                    classNamePrefix="react-select"
                    placeholder="Select user…"
                  />
                </div>
              </div>
            </FormBlock>
          </>
        )}

        {custTab === "people" && (
          <>
            <FormBlock title="Primary contact" subtitle="Main point of contact for this supplier." iconClass="bi-person-lines-fill">
              <div className="row g-3">
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-pn" label="Primary contact name" />
                  <input id="c-pn" className="form-control form-control-sm" value={form.primaryContactName} onChange={(e) => setField("primaryContactName", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-pe" label="Primary contact email" />
                  <input id="c-pe" type="email" className="form-control form-control-sm" value={form.primaryContactEmail} onChange={(e) => setField("primaryContactEmail", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <FieldLabel htmlFor="c-pp" label="Primary contact phone" />
                  <input id="c-pp" className="form-control form-control-sm" value={form.primaryContactPhone} onChange={(e) => setField("primaryContactPhone", e.target.value)} />
                </div>
              </div>
            </FormBlock>

            <FormBlock title="Additional contacts" subtitle="Optional rows for other stakeholders." iconClass="bi-telephone">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">Add as many rows as you need.</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      customerContacts: [...(p.customerContacts || []), emptyContact()],
                    }))
                  }
                >
                  <i className="bi bi-plus-lg me-1" />
                  Add contact
                </button>
              </div>
              {(form.customerContacts || []).map((c) => (
                <div key={c.id} className="row g-2 align-items-end border rounded-3 p-2 mb-2 bg-light-subtle">
                  <div className="col-md-3">
                    <FieldLabel htmlFor={`cn-${c.id}`} label="Name" />
                    <input
                      id={`cn-${c.id}`}
                      className="form-control form-control-sm"
                      value={c.name}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          customerContacts: p.customerContacts.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)),
                        }))
                      }
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldLabel htmlFor={`ce-${c.id}`} label="Email" />
                    <input
                      id={`ce-${c.id}`}
                      className="form-control form-control-sm"
                      value={c.email}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          customerContacts: p.customerContacts.map((x) => (x.id === c.id ? { ...x, email: e.target.value } : x)),
                        }))
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <FieldLabel htmlFor={`cm-${c.id}`} label="Phone" />
                    <input
                      id={`cm-${c.id}`}
                      className="form-control form-control-sm"
                      value={c.mobile}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          customerContacts: p.customerContacts.map((x) => (x.id === c.id ? { ...x, mobile: e.target.value } : x)),
                        }))
                      }
                    />
                  </div>
                  <div className="col-md-3">
                    <FieldLabel htmlFor={`cr-${c.id}`} label="Role" />
                    <select
                      id={`cr-${c.id}`}
                      className="form-select form-select-sm"
                      value={c.role || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          customerContacts: p.customerContacts.map((x) => (x.id === c.id ? { ...x, role: e.target.value } : x)),
                        }))
                      }
                    >
                      {CONTACT_ROLES.map((r) => (
                        <option key={r.value || "z"} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-1 text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={(form.customerContacts || []).length < 2}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          customerContacts: p.customerContacts.filter((x) => x.id !== c.id),
                        }))
                      }
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </FormBlock>

            <FormBlock title="Notes & audit" subtitle="Internal remarks and record history." iconClass="bi-journal-text">
              <div className="mb-3">
                <FieldLabel htmlFor="c-rem" label="Remarks / notes" />
                <textarea id="c-rem" className="form-control form-control-sm" rows={3} value={form.customerRemarks} onChange={(e) => setField("customerRemarks", e.target.value)} />
              </div>
              <div className="row g-2 small rounded-3 border bg-body-tertiary p-3">
                <div className="col-6 col-md-3">
                  <span className="text-muted">Created by</span>
                  <div className="fw-medium">{form.createdBy || "—"}</div>
                </div>
                <div className="col-6 col-md-3">
                  <span className="text-muted">Created on</span>
                  <div className="fw-medium text-break">{form.createdOn || "—"}</div>
                </div>
                <div className="col-6 col-md-3">
                  <span className="text-muted">Updated by</span>
                  <div className="fw-medium">{form.updatedBy || "—"}</div>
                </div>
                <div className="col-6 col-md-3">
                  <span className="text-muted">Updated on</span>
                  <div className="fw-medium text-break">{form.updatedOn || "—"}</div>
                </div>
              </div>
            </FormBlock>
          </>
        )}
      </div>

      <div className="org-sticky-actions rounded-3 mt-3">
        <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <p className="small text-muted mb-0 d-none d-md-block">Use tabs to complete all sections before saving.</p>
          <div className="d-flex flex-wrap gap-2 justify-content-end ms-md-auto">
            <button type="button" className="btn btn-sm btn-outline-secondary px-3" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="btn btn-sm btn-primary px-3" onClick={save}>
              <i className="bi bi-check2-circle me-2" aria-hidden />
              Save supplier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
